import { Box } from '@chakra-ui/react';
import { useState } from 'react';
import { useSelector } from 'react-redux';

import { Error } from '@/components/';
import { nullError } from '@/components/error.js';
import APILoadingView from './APILoadingView.jsx';
import APIResultsView from './APIResultsView.jsx';
import CSVTemplateView from './CSVTemplateView.jsx';
import useCSVCallingRates from './useCSVCallingRates.js';
import useCSVTemplate from './useCSVTemplate.js';
import VerifyCSVTableView from './VerifyCSVTableView.jsx';
import VerifyCSVView from './VerifyCSVView.jsx';

import {
    failedAPIColumns,
    failedColumns,
    passedColumns,
    succeedAPIColumns,
} from './constants';
import useBulkUpdate from './useBulkUpdate.js';

const DOWNLOAD_CSV_TEMPLATE = 'DOWNLOAD_CSV_TEMPLATE';
const LOADING_CSV_VALIDATION = 'LOADING_CSV_VALIDATION';
const CSV_VALIDATION_RESULTS = 'CSV_VALIDATION_RESULTS';
const API_CALLS_PROGRESS = 'API_CALLS_PROGRESS';
const API_CALLS_RESULTS = 'API_CALLS_RESULTS';
const CSV_DATA_NOT_FOUND = 'CSV_DATA_NOT_FOUND';

/** Calling Rate useCSVDisplayControl hook */
const useCSVDisplayControl = () => {
    const [viewVerificationResults, setViewVerificationResults] =
        useState(false);
    const [viewAPIResults, setViewAPIResults] = useState(false);
    // processing csv hook
    const {
        handleUploadData,
        uploadData,
        verificationChecks,
        loading: verificationLoading,
        error: verificationErrors,
        payloads,
        verificationResult,
        handleReset: handleResetCSV,
        handleExportInvalidData,
    } = useCSVCallingRates();
    // api calls hook
    const {
        handlePayloadChange,
        loading: apiLoading,
        counter: completionCounter,
        errorCounter,
        error: apiErrors,
        succeed: apiSucceed,
        handleExportFailedData,
        handleReset: handleResetBulkUpdate,
        total,
    } = useBulkUpdate();
    // button handler to view CSV_VALIDATION_RESULTS
    const handleViewVerificationResults = () => {
        setViewVerificationResults(true);
    };
    // button handler to view API_CALLS_RESULTS
    const handleViewAPIResults = () => {
        setViewAPIResults(true);
    };
    // button handler to reset everything
    const handleStartOver = () => {
        setViewVerificationResults(false);
        setViewAPIResults(false);
        handleResetCSV();
        handleResetBulkUpdate();
    };
    //handles what need to be currently shown
    const handleCurrentDisplay = () => {
        if (verificationErrors) {
            return CSV_DATA_NOT_FOUND;
        }
        if (!uploadData) {
            return DOWNLOAD_CSV_TEMPLATE;
        }

        if (viewAPIResults) {
            return API_CALLS_RESULTS;
        }

        if (apiLoading) {
            return API_CALLS_PROGRESS;
        }

        if (viewVerificationResults) {
            return CSV_VALIDATION_RESULTS;
        }

        if (verificationLoading) {
            return LOADING_CSV_VALIDATION;
        }
    };
    //handle cancel button on CSV_VALIDATION_RESULTS
    const handleCancelValidationTable = () => {
        handleResetCSV();
        setViewVerificationResults(false);
        // window.location.reload();
    };
    //handle transition to start API_CALLS_PROGRESS
    const handleStartAPICall = () => {
        handlePayloadChange(payloads);
        setViewVerificationResults(false);
    };

    return {
        handleUploadData,
        handleCurrentDisplay,
        verificationChecks,
        verificationErrors,
        handleViewVerificationResults,
        verificationResult,
        handleCancel: handleCancelValidationTable,
        handleImport: handleStartAPICall,
        handleExportInvalidData,
        completionCounter,
        payloads,
        errorCounter,
        handleViewAPIResults,
        apiErrors,
        apiSucceed,
        handleExportFailedData,
        handleStartOver,
        total,
    };
};

/* CSVCallingRates*/
function CSVDisplay() {
    const {
        handleUploadData,
        handleCurrentDisplay,
        verificationChecks,
        handleViewVerificationResults,
        verificationResult,
        handleCancel,
        verificationErrors,
        handleImport,
        handleExportInvalidData,
        completionCounter,
        payloads,
        errorCounter,
        handleViewAPIResults,
        apiErrors,
        apiSucceed,
        handleExportFailedData,
        handleStartOver,
        total,
    } = useCSVDisplayControl();

    const currentDisplay = handleCurrentDisplay();
    // hook to download csv
    const { handleDownloadTemplate, loading: templateLoading } =
        useCSVTemplate();
    const { currentCompany } = useSelector((state) => {
        return { ...state.navigationLists };
    });

    if (!currentCompany) {
        return <Error error={nullError} />;
    }

    return (
        <Box id="csv-upload">
            {currentDisplay === DOWNLOAD_CSV_TEMPLATE && (
                <CSVTemplateView
                    handleUploadData={handleUploadData}
                    downloadCSVText="Download"
                    header="CSV Upload - Calling Rates"
                    step1Text="Step 1 - Download the CSV template"
                    step2Text="Step 3 - Upload CSV to be imported"
                    instructionsHeader="Instructions"
                    handleDownloadTemplate={handleDownloadTemplate}
                    loading={templateLoading}
                    bgColor="white"
                    info="Calling Rates"
                    alertText="Define Calling Rates specific to this plan.  Calling rates control Inbound, Outbound and Connection charges per call where this plan applies."
                    instructionsBody={[
                        'Please ensure you keep the original CSV file, in case of data corruption.',
                        'Prefix Example: 61 to match numbers starting in 61, 61[23] matches numbers starting in 612 or 613',
                        'Active: enter 0 to deactivate a rate, 1 to indicate a rate is active. You may need to download the template to view the current rates.',
                        'Rates must be non-negative and should be specified with up to six decimal places for precision.',
                        'You are responsible for the changes you are about to make through this data import.',
                    ]}
                />
            )}
            {currentDisplay === LOADING_CSV_VALIDATION && (
                <VerifyCSVView
                    handleViewVerificationResults={
                        handleViewVerificationResults
                    }
                    verificationChecks={verificationChecks}
                />
            )}
            {currentDisplay === CSV_VALIDATION_RESULTS && (
                <VerifyCSVTableView
                    verificationResult={verificationResult}
                    handleCancel={handleCancel}
                    handleImport={handleImport}
                    handleExportInvalidData={handleExportInvalidData}
                    handleStartOver={handleStartOver}
                    passedColumns={passedColumns}
                    failedColumns={failedColumns}
                />
            )}
            {currentDisplay === API_CALLS_PROGRESS && (
                <APILoadingView
                    completionCounter={completionCounter}
                    payloads={payloads}
                    verificationResult={verificationResult}
                    errorCounter={errorCounter}
                    handleViewAPIResults={handleViewAPIResults}
                    total={total}
                />
            )}
            {currentDisplay === API_CALLS_RESULTS && (
                <APIResultsView
                    error={apiErrors}
                    succeed={apiSucceed}
                    succeedAPIColumns={succeedAPIColumns}
                    failedAPIColumns={failedAPIColumns}
                    handleExportFailedData={handleExportFailedData}
                    handleDone={handleStartOver}
                />
            )}
            {currentDisplay === CSV_DATA_NOT_FOUND && (
                <VerifyCSVView
                    handleStartOver={handleStartOver}
                    errors={verificationErrors}
                />
            )}
        </Box>
    );
}

export default CSVDisplay;
